/** Browser-side projection. Checked against the real model by test:ff035-v2-model. */
export type Cell = { reel: number; row: number };
export type Cluster = { symbol: string; count: number; multiplier: number; winAmount: number; winCells: Cell[] };
export type Step = { window: string[][]; spotHits: number[][]; spotHitsAfter: number[][]; clusterWins: Cluster[]; winCells: Cell[]; totalWinInCredits: number };
export type Config = {
  reelsSetup: number[]; baseSpinsReels: string[][]; freeSpinsReels: string[][];
  paytable: Record<string, number[]>; symbolsReplacedByWild: Record<string, boolean>;
  wild: string; scat: string; coin: string; betMultiplier: number;
  cluster: { minimumSize: number; maximumPayingSize: number }; cascade: { maxSteps: number };
  freeSpinsAwards: Record<number, number>; maxWinInCreditsMultiplier: number;
  coinFeature: { reelsSetup?: number[]; entryLayout?: 'preserve' | 'pack'; coinTrigger: number;
    terminalCoinTriggerMinimumCoins: number; coinTriggerSpinCount: number; coins: (number | string)[] };
};
export const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const wrapIndex = (index: number, length: number) => ((index % length) + length) % length;
export const cellKey = (cell: Cell) => `${cell.reel}:${cell.row}`;
export const multiplierForHits = (hits: number) => hits < 2 ? 0 : 2 ** (hits - 1);
export const emptyHits = (config: Config) => config.reelsSetup.map(height => Array<number>(height).fill(0));
export function visibleWindow(indexes: number[], config: Config, free = false): string[][] {
  const strips = free ? config.freeSpinsReels : config.baseSpinsReels;
  if (indexes.length !== config.reelsSetup.length || indexes.some(n => !Number.isSafeInteger(n))) {
    throw new Error('Choose one integer stop for every reel.');
  }
  return config.reelsSetup.map((height, reel) => {
    const strip = strips[reel];
    if (!strip?.length) throw new Error(`Reel ${reel + 1} has no symbols.`);
    return Array.from({ length: height }, (_, row) => strip[wrapIndex(indexes[reel] + row, strip.length)]);
  });
}
export function evaluateWindow(window: string[][], config: Config, hits: number[][]): Step {
  const clusterWins: Cluster[] = [];
  for (const symbol of Object.keys(config.paytable)) {
    if ([config.wild, config.scat, config.coin].includes(symbol)) continue;
    const seen = new Set<string>();
    window.forEach((column, reel) => column.forEach((value, row) => {
      if (value !== symbol || seen.has(`${reel}:${row}`)) return;
      const pending = [{ reel, row }]; const winCells: Cell[] = [];
      while (pending.length) {
        const cell = pending.pop()!; const key = cellKey(cell);
        const current = window[cell.reel]?.[cell.row];
        if (seen.has(key) || (current !== symbol && !(current === config.wild && config.symbolsReplacedByWild[symbol]))) continue;
        seen.add(key); winCells.push(cell);
        pending.push({ reel: cell.reel - 1, row: cell.row }, { reel: cell.reel + 1, row: cell.row },
          { reel: cell.reel, row: cell.row - 1 }, { reel: cell.reel, row: cell.row + 1 });
      }
      const count = winCells.length;
      if (count < config.cluster.minimumSize) return;
      const table = config.paytable[symbol];
      const payout = table[table.length - Math.min(count, config.cluster.maximumPayingSize)] ?? 0;
      if (payout <= 0) return;
      winCells.sort((a, b) => a.reel - b.reel || a.row - b.row);
      const multiplier = Math.max(1, winCells.reduce((sum, cell) => sum + multiplierForHits(hits[cell.reel][cell.row]), 0));
      clusterWins.push({ symbol, count, multiplier, winAmount: payout * multiplier, winCells });
    }));
  }
  const winCells = Array.from(new Map(clusterWins.flatMap(win => win.winCells).map(cell => [cellKey(cell), cell])).values())
    .sort((a, b) => a.reel - b.reel || a.row - b.row);
  const spotHitsAfter = clone(hits);
  winCells.forEach(({ reel, row }) => { spotHitsAfter[reel][row] = Math.min(11, hits[reel][row] + 1); });
  return { window: clone(window), spotHits: clone(hits), spotHitsAfter, clusterWins, winCells,
    totalWinInCredits: clusterWins.reduce((sum, win) => sum + win.winAmount, 0) };
}
export function previewCascades(indexes: number[], config: Config, free = false, hits = emptyHits(config)): Step[] {
  const strips = free ? config.freeSpinsReels : config.baseSpinsReels;
  let window = visibleWindow(indexes, config, free); let spots = clone(hits);
  const consumed = indexes.map(() => 0); const steps: Step[] = [];
  for (let index = 0; index <= config.cascade.maxSteps; index++) {
    const step = evaluateWindow(window, config, spots); steps.push(step); spots = step.spotHitsAfter;
    if (!step.winCells.length) return steps;
    window = window.map((column, reel) => {
      const removed = new Set(step.winCells.filter(cell => cell.reel === reel).map(cell => cell.row));
      const survivors = column.filter((_, row) => !removed.has(row));
      const replacements = Array.from({ length: removed.size }, () =>
        strips[reel][wrapIndex(indexes[reel] + config.reelsSetup[reel] + consumed[reel]++, strips[reel].length)]);
      return [...replacements.reverse(), ...survivors];
    });
  }
  throw new Error(`This selection exceeds the model's ${config.cascade.maxSteps}-tumble limit.`);
}
export const countSymbol = (window: string[][], symbol: string) => window.flat().filter(value => value === symbol).length;
export function coinEntryWindow(window: string[][], config: Config): string[][] {
  const heights = config.coinFeature.reelsSetup ?? config.reelsSetup;
  if (config.coinFeature.entryLayout === 'preserve') {
    window.forEach((column, reel) => column.forEach((symbol, row) => {
      if (symbol === config.coin && (heights[reel] ?? 0) <= row) throw new Error('The Coin Spins grid cannot preserve these trigger coins.');
    }));
    return heights.map((height, reel) => Array.from({ length: height }, (_, row) => window[reel]?.[row] ?? ''));
  }
  let count = countSymbol(window, config.coin);
  if (count > heights.reduce((sum, height) => sum + height, 0)) throw new Error('The Coin Spins grid has insufficient room for these trigger coins.');
  return heights.map(height => Array.from({ length: height }, () => count-- > 0 ? config.coin : ''));
}
export type SearchKind = 'quiet' | 'cluster' | 'tumbles' | 'multiplier' | 'freespins' | 'coins';
export function findReelStops(config: Config, kind: SearchKind, free = false, hits = emptyHits(config), seed = 1234): number[] | undefined {
  const strips = free ? config.freeSpinsReels : config.baseSpinsReels;
  const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  // Bound the work so searches cannot hang on impossible reel/config combinations.
  const deadline = Date.now() + 1500;
  for (let attempt = 0; attempt < 4000 && Date.now() < deadline; attempt++) {
    const indexes = strips.map(strip => Math.floor(random() * strip.length));
    if (kind === 'freespins' && attempt === 0) {
      // Scatters survive tumbles, so a reachable three-reel trigger is a useful first candidate.
      let selected = 0;
      strips.forEach((strip, reel) => {
        const stop = strip.indexOf(config.scat);
        if (stop >= 0 && selected < 3) { indexes[reel] = stop; selected++; }
      });
    }
    try {
      const steps = previewCascades(indexes, config, free, hits);
      const terminal = steps[steps.length - 1].window;
      const scatters = countSymbol(terminal, config.scat); const coins = countSymbol(terminal, config.coin);
      const matches = kind === 'quiet' ? steps.length === 1 && scatters < 3 && coins < config.coinFeature.terminalCoinTriggerMinimumCoins
        : kind === 'cluster' ? steps.length > 1
          : kind === 'tumbles' ? steps.length >= 4
            : kind === 'multiplier' ? steps.some(step => step.clusterWins.some(win => win.multiplier > 1))
              : kind === 'freespins' ? scatters >= 3
                : coins >= config.coinFeature.terminalCoinTriggerMinimumCoins;
      if (matches) return indexes;
    } catch { /* Skip an overlong cascade; it cannot be queued. */ }
  }
  return undefined;
}
